import { normalizeFirstName, normalizeName } from "../utils/utils";
import { ParticipantWithId } from "../interfaces/IParticipants";
import { useMemo } from "react";
import { useParticipantsContext } from "../contexts/ParticipantsContext";

export interface DuplicateGroup {
  signature: string; // firstNameNorm+lastNameNorm
  reason: "SKRUTINER_ID" | "BIRTHDATE_CONFLICT" | "POSSIBLE_HOMONYM";
  participants: ParticipantWithId[];
}

const buildSignature = (p: ParticipantWithId): string => {
  const first = normalizeFirstName(p.firstName);
  const last = normalizeName(p.lastName);
  return `${first}__${last}`;
};

export const useDuplicateAnalysis = () => {
  const { participants } = useParticipantsContext();

  const groups = useMemo<DuplicateGroup[]>(() => {
    if (!participants?.length) return [];

    const bySignature = new Map<string, ParticipantWithId[]>();
    for (const p of participants.filter((x) => !x.duplicateIgnored)) {
      const sig = buildSignature(p);
      if (!bySignature.has(sig)) bySignature.set(sig, []);
      bySignature.get(sig)!.push(p);
    }

    const result: DuplicateGroup[] = [];

    for (const [signature, list] of Array.from(bySignature.entries())) {
      if (list.length <= 1) continue;

      // Konflikt dat urodzenia
      const uniqueBirths = new Set(
        list.map((p: ParticipantWithId) => new Date(p.birthDate).toISOString().slice(0, 10))
      );
      if (uniqueBirths.size > 1) {
        result.push({ signature, reason: "BIRTHDATE_CONFLICT", participants: list });
        continue;
      }

      // Potencjalny homonim (ten sam podpis, ale tu brak dodatkowych sygnałów)
      result.push({ signature, reason: "POSSIBLE_HOMONYM", participants: list });
    }

    return result;
  }, [participants]);

  const duplicatesCount = groups.length;

  return { groups, duplicatesCount };
};
